import { Link } from 'react-router-dom'
import { rupturaBand, RUPTURA_TARGET, type RupturaForecast, type RupturaBand } from '../../lib/rupturaForecast'

// Card da aba "Pedidos" / Miguel: situação da ruptura ao longo do tempo de forma
// EXPLICATIVA (sem gráfico de linha preciso). Mostra 3 marcos em faixas coloridas
// vs a meta — Hoje · Melhor ponto (após as entregas, líquido da demanda) · Fim do
// horizonte — com uma frase de veredito em linguagem simples. Compartilhado entre
// desktop e mobile (`compact`).

const fmtPct = (frac: number) => `${(frac * 100).toFixed(0)}%`
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

const BAND_DOT: Record<RupturaBand, string> = {
  verde: 'bg-emerald-500',
  ambar: 'bg-amber-500',
  vermelho: 'bg-red-500',
}
const BAND_TEXT: Record<RupturaBand, string> = {
  verde: 'text-emerald-600 dark:text-emerald-400',
  ambar: 'text-amber-600 dark:text-amber-400',
  vermelho: 'text-red-600 dark:text-red-400',
}
const BAND_CHIP: Record<RupturaBand, string> = {
  verde: 'bg-emerald-500/10 border-emerald-500/30',
  ambar: 'bg-amber-500/10 border-amber-500/30',
  vermelho: 'bg-red-500/10 border-red-500/30',
}

interface Marco {
  label: string
  index: number
  date: string | null
}

export default function RupturaForecastCard({
  forecast,
  compact = false,
  title = 'Previsão de ruptura',
  target = RUPTURA_TARGET,
  showCta = true,
}: {
  forecast: RupturaForecast
  compact?: boolean
  title?: string
  target?: number
  showCta?: boolean
}) {
  const f = forecast
  const semRuptura = f.rupturaTotal === 0 && f.minIndex === 0 && f.endIndex === 0

  // 3 marcos no tempo (omite "melhor" se for igual ao de hoje).
  const marcos: Marco[] = [
    { label: 'Hoje', index: f.currentIndex, date: null },
    { label: 'Melhor ponto', index: f.minIndex, date: f.minIndexDate },
    { label: `Em ${f.horizonDays}d`, index: f.endIndex, date: f.endDate },
  ]

  // Veredito em linguagem simples.
  const metaPct = fmtPct(target)
  let verdict: string
  if (semRuptura) {
    verdict = 'Sem itens em ruptura no horizonte projetado.'
  } else {
    const hojeOk = f.currentIndex <= target
    const fimOk = f.endIndex <= target
    const base = `Hoje ${fmtPct(f.currentIndex)} ${hojeOk ? 'dentro da' : 'acima da'} meta de ${metaPct}.`
    if (f.eliminationDate) {
      verdict = `${base} Com os pedidos a caminho, zera em ${fmtDate(f.eliminationDate)}` +
        (fimOk ? ' e fecha o horizonte na meta.' : `, mas fecha o horizonte em ${fmtPct(f.endIndex)}.`)
    } else if (f.minIndex < f.currentIndex) {
      verdict = `${base} Melhora para ${fmtPct(f.minIndex)} em ${fmtDate(f.minIndexDate)}` +
        (fimOk ? ' e fecha na meta.' : `, mas fecha o horizonte em ${fmtPct(f.endIndex)} — ${f.endIndex <= target ? 'na meta' : 'ainda acima'}.`)
    } else {
      verdict = `${base} Sem entregas que reduzam: tende a ${fmtPct(f.endIndex)} em ${f.horizonDays} dias.`
    }
  }

  return (
    <div className={`bg-surface border border-line rounded-xl ${compact ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wide text-ink-faint">
          {title} · próximos {f.horizonDays}d
        </p>
        <span className="text-[10px] text-ink-faint">meta ≤ {metaPct}</span>
      </div>

      {semRuptura ? (
        <p className={`mt-1 ${compact ? 'text-lg' : 'text-xl'} font-bold leading-tight text-emerald-600 dark:text-emerald-400`}>
          Sem ruptura projetada
        </p>
      ) : (
        <>
          {/* Marcos no tempo em faixas coloridas vs a meta */}
          <div className="mt-2 grid grid-cols-3 gap-2">
            {marcos.map((m, i) => {
              const band = rupturaBand(m.index)
              return (
                <div key={i} className={`rounded-lg border px-2.5 py-2 ${BAND_CHIP[band]}`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${BAND_DOT[band]}`} />
                    <span className="text-[10px] uppercase tracking-wide text-ink-faint truncate">{m.label}</span>
                  </div>
                  <p className={`text-lg font-bold tabular-nums leading-tight ${BAND_TEXT[band]}`}>{fmtPct(m.index)}</p>
                  <p className="text-[10px] text-ink-faint leading-tight">{m.date ? fmtDate(m.date) : 'agora'}</p>
                </div>
              )
            })}
          </div>

          {/* Trilho com a linha da meta */}
          <div className="mt-2 relative h-1.5 rounded-full bg-sunken overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${BAND_DOT[rupturaBand(f.endIndex)]}`}
              style={{ width: `${Math.min(100, (f.endIndex / Math.max(target * 3, f.currentIndex, 0.0001)) * 100)}%` }}
            />
            <div className="absolute inset-y-0 border-l border-dashed border-ink-faint" style={{ left: `${Math.min(100, (target / Math.max(target * 3, f.currentIndex, 0.0001)) * 100)}%` }} />
          </div>

          <p className="mt-2 text-xs text-ink-muted leading-snug">{verdict}</p>
        </>
      )}

      {(f.semDataCount > 0 || (showCta && (f.minIndex > target || f.endIndex > target))) && (
        <p className="mt-1.5 text-[11px] text-ink-faint">
          {f.semDataCount > 0 && (
            <>{f.semDataCount} {f.semDataCount === 1 ? 'item em ruptura com pedido sem data prevista' : 'itens em ruptura com pedido sem data prevista'} — fora da curva. </>
          )}
          {showCta && (f.minIndex > target || f.endIndex > target) && (
            <Link to="/miguel-comprador" className="underline hover:text-ink-muted whitespace-nowrap">
              Ver no Comprador
            </Link>
          )}
        </p>
      )}
    </div>
  )
}
